import type { RoutePattern } from '@/services/router/match-route';
import { matchRoutes } from '@/services/router/match-route';

/**
 * Static list of generic suggestions shown when no route-specific suggestions apply.
 */
export const STATIC_SUGGESTIONS = [
  { label: 'What is a journey?', message: 'What is a customer journey in Customer.io?' },
  { label: 'How do I send emails?', message: 'How do I send my first email campaign?' },
  { label: 'What are segments?', message: 'What are customer segments and how do I create them?' },
  { label: 'How do I track events?', message: 'How do I track custom events in Customer.io?' },
  { label: 'What is A/B testing?', message: 'What is A/B testing and how do I set it up?' },
  { label: 'How do I use the API?', message: 'How do I integrate Customer.io with my application using the API?' },
  { label: 'What is Design Studio?', message: 'What is Design Studio and how do I use it?' },
  { label: 'How do I send SMS?', message: 'How do I send SMS campaigns to my customers?' },
  { label: 'What are webhooks?', message: 'What are webhooks and how do I configure them?' },
  { label: 'How do I add people?', message: 'How do I import and add customers to Customer.io?' },
  { label: 'What are attributes?', message: 'What are customer attributes and how do I manage them?' },
  { label: 'How do I test campaigns?', message: 'How do I test my campaigns before sending them?' },
  { label: 'What is personalization?', message: 'What is message personalization and how do I use it?' },
  { label: 'How do I schedule sends?', message: 'How do I schedule campaigns to send at specific times?' },
  { label: 'What are triggers?', message: 'What are campaign triggers and how do they work?' },
  { label: 'How do I handle unsubscribes?', message: 'How do I handle unsubscribe requests properly?' },
  { label: 'What are transactional emails?', message: 'What are transactional emails and how do I send them?' },
  { label: 'How do I measure success?', message: 'How do I measure and analyze campaign performance?' },
  { label: 'What integrations exist?', message: 'What third-party tools can I integrate with Customer.io?' },
  { label: 'How do I manage my team?', message: 'How do I add team members and manage permissions?' },
];

interface PageSuggestionGroup {
  routes: RoutePattern[];
  suggestions: Array<{ label: string; message: string }>;
}

/**
 * Route-specific suggestions matched by Route patterns.
 *
 * Routes ending with `/*` use prefix matching (any sub-path matches).
 * All other routes use exact matching.
 * Groups are checked in order — the first match wins.
 *
 * Only includes suggestions that are generic enough to work without workspace-specific state.
 */
const PAGE_SUGGESTIONS: PageSuggestionGroup[] = [
  // ── Dashboard / Home ──────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/dashboard', '/workspaces/:workspaceId/journeys/home/*'],
    suggestions: [
      {
        label: 'Plan a messaging calendar',
        message: 'Help me plan a 30-day messaging calendar based on best practices for customer engagement',
      },
      {
        label: 'Untargeted segments?',
        message: 'How can I find audience segments that are not being targeted by any active campaign?',
      },
      {
        label: 'Gap analysis',
        message: "What's the next campaign I should build to fill a gap in my customer journey?",
      },
      {
        label: 'Re-engage inactive users',
        message: 'What type of campaign is most effective to re-engage inactive users?',
      },
      { label: 'Measure weekly performance', message: 'How do I set up a weekly summary of my messaging performance?' },
    ],
  },

  // ── Campaign — Workflow Builder ───────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/setup/workflow/actions',
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/workflow',
      '/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/workflow/actions',
    ],
    suggestions: [
      {
        label: 'Review my workflow',
        message: 'What are common logical gaps or missing steps to look for when reviewing a campaign workflow?',
      },
      { label: 'Ideal email delay', message: "What's the ideal delay between emails in an onboarding sequence?" },
      {
        label: 'Branch on click',
        message: 'How do I add a branch that sends different messages based on whether someone clicked?',
      },
      {
        label: 'Write a subject line',
        message: 'Write a compelling subject line and preview text for an onboarding email',
      },
      {
        label: 'Prevent re-entry',
        message: "How do I stop someone from re-entering a campaign if they've already finished it?",
      },
    ],
  },

  // ── Campaigns — List / New / Welcome (before detail — /new matches :campaignId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/campaigns',
      '/workspaces/:workspaceId/journeys/campaigns/new',
      '/workspaces/:workspaceId/journeys/campaigns/welcome/*',
    ],
    suggestions: [
      {
        label: 'Re-engage inactive users',
        message: 'What type of campaign is most effective to re-engage inactive users?',
      },
      { label: 'Onboarding sequence', message: "What's the ideal campaign sequence for a new user's first 60 days?" },
      { label: 'Event-triggered campaigns', message: 'How do I set up event-triggered campaigns in Customer.io?' },
      {
        label: 'Clean up old drafts',
        message: 'How do I find and clean up campaigns that have been in draft for a long time?',
      },
      {
        label: 'Campaign best practices',
        message: 'What are the best practices for organizing and naming campaigns in Customer.io?',
      },
    ],
  },

  // ── Campaign — Detail / Overview ──────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/campaigns/:campaignId/*'],
    suggestions: [
      {
        label: 'Improve conversions',
        message: 'What are 3 specific changes I could make to improve conversions on a campaign?',
      },
      {
        label: 'Diagnose open rate drop',
        message: 'What are common reasons for an email open rate drop and how do I fix it?',
      },
      {
        label: 'Reduce drop-off',
        message: 'How do I identify which message in a workflow is causing the most drop-off?',
      },
      { label: 'A/B test ideas', message: 'Help me design an A/B test hypothesis for improving a campaign workflow' },
      {
        label: 'Add a conversion goal',
        message: 'What goal or conversion event should I add to make my campaign smarter?',
      },
    ],
  },

  // ── Broadcast / Newsletter — Detail ───────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/*',
      '/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId/*',
    ],
    suggestions: [
      {
        label: 'Follow-up message',
        message: 'Draft a follow-up message for people who opened a broadcast but did not click',
      },
      { label: 'Subject line variants', message: 'Suggest 3 subject line variants I could test in my next broadcast' },
      { label: 'Compare performance', message: "How do I compare a broadcast's performance to my previous sends?" },
      {
        label: 'Improve next send',
        message: 'What would I change about a broadcast to improve results on the next send?',
      },
      {
        label: 'Segment targeting',
        message: 'How do I determine which audience segments respond best to a broadcast?',
      },
    ],
  },

  // ── Broadcasts — List ─────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/broadcasts/*'],
    suggestions: [
      {
        label: 'Best send time',
        message: "What's the best day and time to send a broadcast based on general engagement patterns?",
      },
      {
        label: 'Re-engagement broadcast',
        message: 'Help me plan a re-engagement broadcast — who should I target and what should I say?',
      },
      { label: 'Broadcast calendar', message: 'Help me plan a broadcast calendar for the next 8 weeks' },
      { label: 'Broadcast vs campaign', message: 'When should I use a broadcast vs a triggered campaign?' },
      { label: 'Newsletter best practices', message: 'What are best practices for newsletters in Customer.io?' },
    ],
  },

  // ── Segments — List / New / Explore (before detail — /new matches :segmentId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/segments',
      '/workspaces/:workspaceId/journeys/segments/new',
      '/workspaces/:workspaceId/journeys/segments/explore/*',
    ],
    suggestions: [
      {
        label: 'Consolidate segments',
        message: 'How can I find segments that overlap significantly and could be consolidated?',
      },
      {
        label: 'Build a churn segment',
        message: 'Build me a segment of users who signed up 30+ days ago but have never converted',
      },
      {
        label: 'Launch segmentation',
        message: 'Help me design a segmentation strategy for an upcoming product launch',
      },
      { label: 'Unused segments', message: 'How do I find segments that are not being used in any active campaign?' },
      { label: 'Segment best practices', message: 'What are best practices for organizing and maintaining segments?' },
    ],
  },

  // ── Segment — Detail ──────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/segments/:segmentId/*'],
    suggestions: [
      { label: 'Tighten conditions', message: 'How can I tighten segment conditions to make it more precise?' },
      {
        label: 'Draft a targeted email',
        message: 'Draft an email that would resonate specifically with a highly engaged audience segment',
      },
      {
        label: 'Best campaign for segment',
        message: "What's the best campaign type I could run against a specific segment?",
      },
      { label: 'Segment too small?', message: 'What are common reasons a segment might be smaller than expected?' },
      {
        label: 'Campaigns using segment',
        message: 'How do I find which campaigns are currently using a specific segment?',
      },
    ],
  },

  // ── People — List / New / Merge (before detail — /new matches :personId)
  {
    routes: [
      '/workspaces/:workspaceId/journeys/people',
      '/workspaces/:workspaceId/journeys/people/new',
      '/workspaces/:workspaceId/journeys/people/create',
      '/workspaces/:workspaceId/journeys/people/merge',
      '/workspaces/:workspaceId/journeys/people/welcome',
    ],
    suggestions: [
      {
        label: 'Find unconverted users',
        message: 'How do I find users who signed up a long time ago but have never converted?',
      },
      { label: 'Expiring subscriptions', message: 'How do I find users whose subscription or trial expires soon?' },
      {
        label: 'Unsubscribe patterns',
        message: 'How can I identify patterns among customers who have recently unsubscribed?',
      },
      { label: 'Incomplete profiles', message: 'How do I find users with incomplete profiles missing key attributes?' },
      { label: 'Import people', message: 'How do I import people into Customer.io?' },
    ],
  },

  // ── Person — Detail ───────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/people/:personId/*'],
    suggestions: [
      { label: 'Activity summary', message: "How do I interpret a person's activity timeline in Customer.io?" },
      {
        label: 'Campaign engagement',
        message: 'How can I see which campaigns a specific person has received and engaged with?',
      },
      {
        label: 'Active workflows',
        message: 'How do I check if a person is currently inside any active campaign workflows?',
      },
      {
        label: 'Re-engage this user',
        message: "What's the best approach to re-engage a user based on their behavior history?",
      },
      {
        label: 'Why did they unsubscribe?',
        message: "What can a person's history tell me about why they might have unsubscribed?",
      },
    ],
  },

  // ── Content / Templates ───────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/message-library',
      '/workspaces/:workspaceId/journeys/assets',
      '/workspaces/:workspaceId/journeys/layouts/*',
      '/workspaces/:workspaceId/journeys/snippets/*',
      '/workspaces/:workspaceId/journeys/template/:templateId',
    ],
    suggestions: [
      {
        label: 'Archive unused templates',
        message: 'How can I find templates that are not used in any active campaign?',
      },
      { label: 'Win-back email series', message: 'Create a template outline for a 3-part win-back email series' },
      {
        label: 'Template consistency',
        message: 'What are best practices for keeping email templates consistent across campaigns?',
      },
      { label: 'Launch templates', message: 'What templates do I need for an upcoming product launch?' },
      { label: 'Broken links', message: 'How do I check my templates for outdated copy or broken links?' },
    ],
  },

  // ── Design Studio ─────────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/design-studio/dashboard',
      '/workspaces/:workspaceId/design-studio/editor/*',
      '/workspaces/:workspaceId/design-studio/welcome',
      '/workspaces/:workspaceId/design-studio/global-styles',
    ],
    suggestions: [
      { label: 'Improve subject line', message: 'How do I write a compelling email subject line and preview text?' },
      { label: 'Rewrite for clarity', message: 'How do I make email copy more concise and action-oriented?' },
      { label: 'A/B test version', message: 'How do I write an alternative version of an email for an A/B test?' },
      {
        label: 'Spam trigger words',
        message: 'What are common spam trigger words that could hurt email deliverability?',
      },
      {
        label: 'Conditional content',
        message: 'How do I add conditional content blocks that show different content based on user attributes?',
      },
    ],
  },

  // ── Transactional Messages ────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/transactional/*'],
    suggestions: [
      {
        label: 'Password reset email',
        message: 'How do I write a clear, on-brand password reset email that reduces friction?',
      },
      {
        label: 'Personalization variables',
        message: 'What personalization variables should I include in transactional messages?',
      },
      {
        label: 'Fallback values',
        message: 'How do I add a safe fallback for when a personalization variable is missing?',
      },
      {
        label: 'Test without sending',
        message: 'How do I test a transactional message without sending it to a real customer?',
      },
      {
        label: 'Transactional vs marketing',
        message: 'What is the difference between transactional and marketing messages?',
      },
    ],
  },

  // ── Analysis / Metrics ────────────────────────────────────────────────
  {
    routes: ['/workspaces/:workspaceId/journeys/analysis', '/workspaces/:workspaceId/analysis/deliverability'],
    suggestions: [
      {
        label: 'Deliverability health',
        message: 'What does good email deliverability look like and what are red flags to watch for?',
      },
      { label: 'High bounce rate', message: 'My bounce rate looks high — what does that mean and what should I do?' },
      { label: 'Best performing campaigns', message: 'How do I identify which campaigns are performing best and why?' },
      {
        label: 'Engagement by segment',
        message: 'How do I compare engagement rates across different audience segments?',
      },
      { label: 'Weekly summary', message: 'How do I build a weekly performance summary to share with my team?' },
    ],
  },

  // ── Data & Integrations ───────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/data-index/*',
      '/workspaces/:workspaceId/journeys/integrations/*',
      '/workspaces/:workspaceId/journeys/imports/*',
      '/workspaces/:workspaceId/journeys/exports/*',
      '/workspaces/:workspaceId/journeys/collections/*',
    ],
    suggestions: [
      {
        label: 'Unused events',
        message: 'How do I find tracked events that are not being used in any campaign trigger?',
      },
      {
        label: 'Event schema design',
        message: 'Help me design an event schema for tracking steps in an onboarding flow',
      },
      { label: 'Churn prevention data', message: 'What data do I need to power a churn prevention campaign?' },
      {
        label: 'Verify event data',
        message: 'How do I verify that event data is arriving correctly and in the right format?',
      },
      {
        label: 'Stale events',
        message: 'How can I identify tracked events that have not fired recently and might be broken?',
      },
    ],
  },

  // ── Settings ──────────────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/journeys/settings/*',
      '/workspaces/:workspaceId/settings/ai-business-profile',
      '/workspaces/:workspaceId/settings/audit-logs',
      '/settings/audit-logs',
      '/settings/account/*',
      '/settings/team/*',
      '/settings/billing/*',
      '/settings/security/*',
      '/settings/personal/*',
      '/settings/roles/*',
      '/settings/privacy',
      '/settings/workspaces/*',
      '/settings/subscription',
      '/settings/receipts',
      '/settings/api_credentials',
      '/settings/experimental-features',
    ],
    suggestions: [
      {
        label: 'Sending domain setup',
        message: 'What sending domain settings should I configure to maximize deliverability?',
      },
      {
        label: 'Custom unsubscribe page',
        message: 'How do I set up a custom unsubscribe page that matches our brand?',
      },
      {
        label: 'Team permissions',
        message: 'How do I add a team member and which permission level should I give them?',
      },
      {
        label: 'Subscription topics',
        message: 'What is the difference between subscription topics and a global unsubscribe?',
      },
      { label: 'GDPR compliance', message: 'What do I need to configure in my workspace to be GDPR-compliant?' },
    ],
  },

  // ── Pipelines (CDP) ───────────────────────────────────────────────────
  {
    routes: [
      '/workspaces/:workspaceId/pipelines/dashboard',
      '/workspaces/:workspaceId/pipelines/setup-guide/*',
      '/workspaces/:workspaceId/pipelines/sources/*',
      '/workspaces/:workspaceId/pipelines/destinations/*',
      '/workspaces/:workspaceId/pipelines/connections',
      '/workspaces/:workspaceId/pipelines/catalog',
    ],
    suggestions: [
      { label: 'What are pipelines?', message: 'What is Customer.io Data Pipelines and how does it work?' },
      { label: 'Add a source', message: 'How do I add a new data source in Data Pipelines?' },
      { label: 'Add a destination', message: 'How do I connect a destination to send data to?' },
      {
        label: 'Source vs destination',
        message: 'What is the difference between a source and a destination in Data Pipelines?',
      },
      { label: 'Reverse ETL', message: 'What is reverse ETL and how do I set it up in Customer.io?' },
    ],
  },
];

const MARQUEE_ROW_COUNT = 3;
/** Number of route-specific suggestions to mix into the marquee when a route matches */
const ROUTE_SPECIFIC_COUNT = 8;
/** Number of generic suggestions to mix in alongside route-specific ones */
const GENERIC_MIX_COUNT = 12;

/**
 * Get 3 random static suggestions for initial page load (used in next-message suggestions).
 * When a pathname is provided, returns route-specific suggestions if available.
 */
export function getInitialStaticSuggestions(pathname?: string) {
  const routeSuggestions = pathname != null ? getRouteSuggestions(pathname) : [];
  const pool = routeSuggestions.length > 0 ? routeSuggestions : STATIC_SUGGESTIONS;
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
}

export const HUMAN_SUPPORT_SUGGESTION = {
  label: 'Human support',
  message: 'Talk to a human support agent',
} as const;

export interface IndexedSuggestion {
  index: number;
  label: string;
  message: string;
}

/**
 * Get route-specific suggestions for the current pathname.
 * Checks each group in order and returns the first match.
 */
function getRouteSuggestions(pathname: string): Array<{ label: string; message: string }> {
  for (const group of PAGE_SUGGESTIONS) {
    if (matchRoutes(pathname, group.routes)) {
      return group.suggestions;
    }
  }
  return [];
}

/**
 * Get static suggestions distributed into rows for the marquee animation.
 * Each suggestion gets a unique index for analytics tracking.
 *
 * When a pathname is provided and matches a route group:
 * - Mixes route-specific suggestions with a subset of generic ones
 * - Route-specific suggestions are prioritized
 *
 * When no pathname is provided or no route matches:
 * - Falls back to the full set of generic suggestions
 */
export function getMarqueeSuggestionRows({
  shouldIncludeHumanSupport = false,
  pathname,
}: {
  shouldIncludeHumanSupport?: boolean;
  pathname?: string;
} = {}): IndexedSuggestion[][] {
  const routeSuggestions = pathname != null ? getRouteSuggestions(pathname) : [];

  let pool: Array<{ label: string; message: string }>;

  if (routeSuggestions.length > 0) {
    // Mix route-specific suggestions with a subset of generic ones
    const shuffledRoute = [...routeSuggestions].sort(() => Math.random() - 0.5);
    const routeLabels = new Set(shuffledRoute.map((s) => s.label));
    const genericPool = STATIC_SUGGESTIONS.filter((s) => !routeLabels.has(s.label));
    const shuffledGeneric = genericPool.sort(() => Math.random() - 0.5);

    pool = [...shuffledRoute.slice(0, ROUTE_SPECIFIC_COUNT), ...shuffledGeneric.slice(0, GENERIC_MIX_COUNT)];
  } else {
    pool = [...STATIC_SUGGESTIONS];
  }

  if (shouldIncludeHumanSupport) {
    pool.push(HUMAN_SUPPORT_SUGGESTION);
  }

  const shuffled = pool.sort(() => Math.random() - 0.5);
  const indexed = shuffled.map((s, index) => ({ ...s, index: index + 1 }));
  const rows: IndexedSuggestion[][] = [];
  const perRow = Math.ceil(indexed.length / MARQUEE_ROW_COUNT);

  for (let index = 0; index < MARQUEE_ROW_COUNT; index++) {
    rows.push(indexed.slice(index * perRow, (index + 1) * perRow));
  }

  return rows;
}
